import React, { useState } from 'react';

const OnlineScoutDashboard = () => {
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyLog, setDailyLog] = useState({
    date: new Date().toISOString().split('T')[0],
    groupsFound: '',
    groupsJoined: '',
    commentsPosted: '',
    messagesSent: '',
    leadsCollected: '',
    positiveResponses: '',
    whatsappJoins: '',
    wins: '',
    challenges: '',
    notes: ''
  });
  const [pendingPosts, setPendingPosts] = useState([
    { id: 1, content: '', status: 'draft', approvedBy: null, approvedAt: null }
  ]);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostPlatform, setNewPostPlatform] = useState('facebook');

  const targetAreas = {
    liberia: ['Monrovia (Capital)', 'Foya', 'Voinjama', 'Kolahun', 'Zorzor'],
    sierraLeone: ['Freetown (Capital)', 'Koindu', 'Kailahun', 'Kenema', 'Segbwema'],
    guinea: ['Conakry (Capital)', 'Guéckédou', 'Kissidougou', 'Macenta', "N'Zérékoré"],
    kissiDiaspora: [
      'USA (New York, Philadelphia, Minnesota, Texas)',
      'UK (London, Manchester)',
      'France (Paris, Lyon)',
      'Germany (Hamburg, Berlin)',
      'Canada (Toronto, Montreal)',
      'Australia (Sydney, Melbourne)',
      'Ghana (Accra)',
      'Nigeria (Lagos)',
      'Ivory Coast (Abidjan)',
      'Senegal (Dakar)'
    ]
  };

  const messageTemplates = [
    {
      id: 1,
      name: 'Church/Faith Community',
      language: 'English',
      template: `Peace be with you! 🙏

I noticed you're active in [Group Name]. I work with Kioo Radio 98.1 FM - we broadcast Christian programs, Bible teaching, and community news across the Makona River Region.

If you'd like to listen online or share with your church:
🎧 Stream: kiooradio.org
📱 WhatsApp: [NUMBER]

We also read prayer requests on air. God bless you!`
    },
    {
      id: 2,
      name: 'Kissi People / Diaspora',
      language: 'English/Kissi',
      template: `Greetings to all Kissi people! 🙏🇱🇷🇸🇱🇬🇳

I'm reaching out from Kioo Radio 98.1 FM - the voice of the Makona River Region! We broadcast in KISSI language every day, along with English and French.

📻 Programs in Kissi:
• Thru the Bible (TTB) - Kissi: 8:00 AM & 8:00 PM GMT
• Kissi Community Programs
• Kissi Phone-in Shows
• Kissi Music & Worship

Stay connected to home and your language:
🎧 Stream FREE: kiooradio.org
📱 WhatsApp: [NUMBER]

Where are you listening from? Share with other Kissi people! 🌍`
    },
    {
      id: 3,
      name: 'Youth/Community Groups',
      language: 'English',
      template: `Hello! 👋

I saw your post in [Group Name] and thought you might enjoy Kioo Radio 98.1 FM. We have programs in English, French, and Kissi - including youth shows, music, and community discussions.

Listen anytime at kiooradio.org or join our WhatsApp for updates: [NUMBER]

What programs would interest you most?`
    },
    {
      id: 4,
      name: 'Diaspora Communities',
      language: 'English',
      template: `Greetings from home! 🌍

Missing news from [Liberia/Sierra Leone/Guinea]? Kioo Radio 98.1 FM broadcasts daily updates, local music, and community voices from the Makona River Region.

Stream free at: kiooradio.org
WhatsApp community: [NUMBER]

Stay connected to home! 📻`
    },
    {
      id: 5,
      name: 'French - Communauté',
      language: 'French',
      template: `Bonjour! 🙏

Je travaille avec Kioo Radio 98.1 FM - une radio chrétienne qui diffuse dans la région de Makona (Libéria, Sierra Leone, Guinée).

Écoutez en direct: kiooradio.org
WhatsApp: [NUMBER]

Nous avons des programmes en français tous les jours!
Que Dieu vous bénisse! 📻`
    },
    {
      id: 6,
      name: 'Kissi Cultural Groups',
      language: 'English',
      template: `Hello Kissi family! 🤝

Are you interested in preserving Kissi language and culture? Kioo Radio 98.1 FM broadcasts daily programs in the Kissi language from the heart of Kissi land!

We feature:
• Kissi Bible teaching
• Kissi traditional wisdom
• Kissi music and songs
• Community news in Kissi

Help us reach more Kissi people worldwide:
🎧 kiooradio.org (FREE streaming)
📱 WhatsApp: [NUMBER]

Together we keep our language alive! 🌍`
    },
    {
      id: 7,
      name: 'Comment Response',
      language: 'English',
      template: `That's wonderful! If you enjoy Christian music and teaching, check out Kioo Radio 98.1 FM - we broadcast across the Makona River Region in English, French, and Kissi. Free streaming at kiooradio.org 📻🙏`
    }
  ];

  const searchQueries = [
    { area: 'Liberia', queries: ['"Monrovia" community', '"Foya Liberia" community', '"Lofa County" news', '"Liberian Christians"'] },
    { area: 'Sierra Leone', queries: ['"Freetown" community', '"Koindu Sierra Leone"', '"Kailahun" news', '"Sierra Leone gospel"'] },
    { area: 'Guinea', queries: ['"Conakry" Guinée', '"Guéckédou" communauté', '"Nzérékoré" actualités', '"Guinea église"'] },
    { area: 'Kissi People', queries: ['"Kissi people"', '"Kissi tribe"', '"Kissi language"', '"Kissi culture"', '"Gissi"', '"Kisi people"'] },
    { area: 'Kissi Diaspora USA', queries: ['"Kissi USA"', '"Liberian Kissi America"', '"Kissi New York"', '"Kissi Philadelphia"', '"Kissi Minnesota"'] },
    { area: 'Kissi Diaspora Europe', queries: ['"Kissi UK"', '"Kissi London"', '"Kissi France"', '"Kissi Paris"', '"Kissi Germany"'] },
    { area: 'Kissi Diaspora Africa', queries: ['"Kissi Ghana"', '"Kissi Nigeria"', '"Kissi Ivory Coast"', '"Kissi Abidjan"'] },
    { area: 'Makona Region', queries: ['"Makona River"', '"Makona youth"', '"Mano River Union"', '"border region Liberia Sierra Leone Guinea"'] }
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-kioo-primary to-green-600 rounded-xl p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                🔍 Online Scout Dashboard
              </h1>
              <p className="text-green-100 mt-2">
                Audience Growth Module | Kioo Radio 98.1 FM
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-green-200">Today's Date</p>
              <p className="text-2xl font-bold">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { id: 'suggestions', label: '📝 Daily Suggestions', icon: '📝' },
            { id: 'guidelines', label: '⛪ Content Guidelines', icon: '⛪' },
            { id: 'approval', label: '✅ Post Approval', icon: '✅' },
            { id: 'daily', label: '📋 Daily Log', icon: '📋' },
            { id: 'templates', label: '💬 Message Templates', icon: '💬' },
            { id: 'search', label: '🔍 Search Guide', icon: '🔍' },
            { id: 'targets', label: '📍 Target Areas', icon: '📍' },
            { id: 'rules', label: '⚠️ Engagement Rules', icon: '⚠️' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-kioo-primary text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Daily Suggestions Tab - NEW */}
        {activeTab === 'suggestions' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                📝 Daily Post Suggestions
              </h2>
              <p className="text-purple-200">
                Ready-to-use posts in English & French. Copy, customize, and submit for approval!
              </p>
              <p className="text-yellow-300 mt-2 font-bold">
                ⚠️ Remember: All posts require human approval before publishing
              </p>
            </div>

            {/* Today's Date */}
            <div className="bg-white rounded-xl shadow-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Today's Suggested Posts</p>
                <p className="text-2xl font-bold">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-500 text-sm">Day of Week</p>
                <p className="text-xl font-bold text-kioo-primary">{new Date().toLocaleDateString('en-US', { weekday: 'long' })}</p>
              </div>
            </div>

            {/* Morning Devotional Post */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-orange-500 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">☀️ Morning Encouragement Post</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    ☀️ Good morning, Makona River Region! 🙏<br/><br/>
                    "This is the day the LORD has made; let us rejoice and be glad in it." - Psalm 118:24<br/><br/>
                    Start your day with Kioo Radio 98.1 FM!<br/>
                    📻 Morning Devotional: 5:00 AM GMT<br/>
                    📖 Thru the Bible: 6:00 AM GMT<br/><br/>
                    What are you thankful for today? Share below! 👇<br/><br/>
                    🎧 Stream FREE: kiooradio.org<br/>
                    #KiooRadio #MorningBlessing #Faith
                  </p>
                  <button onClick={() => copyToClipboard(`☀️ Good morning, Makona River Region! 🙏\n\n"This is the day the LORD has made; let us rejoice and be glad in it." - Psalm 118:24\n\nStart your day with Kioo Radio 98.1 FM!\n📻 Morning Devotional: 5:00 AM GMT\n📖 Thru the Bible: 6:00 AM GMT\n\nWhat are you thankful for today? Share below! 👇\n\n🎧 Stream FREE: kiooradio.org\n#KiooRadio #MorningBlessing #Faith`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    ☀️ Bonjour, Région de la Rivière Makona! 🙏<br/><br/>
                    "C'est ici la journée que l'Éternel a faite; réjouissons-nous et soyons dans l'allégresse!" - Psaume 118:24<br/><br/>
                    Commencez votre journée avec Kioo Radio 98.1 FM!<br/>
                    📻 Méditation Matinale: 5h00 GMT<br/>
                    📖 À Travers la Bible: 6h00 GMT<br/><br/>
                    Pour quoi êtes-vous reconnaissant aujourd'hui? Partagez ci-dessous! 👇<br/><br/>
                    🎧 Écoutez GRATUITEMENT: kiooradio.org<br/>
                    #KiooRadio #BénédictionMatinale #Foi
                  </p>
                  <button onClick={() => copyToClipboard(`☀️ Bonjour, Région de la Rivière Makona! 🙏\n\n"C'est ici la journée que l'Éternel a faite; réjouissons-nous et soyons dans l'allégresse!" - Psaume 118:24\n\nCommencez votre journée avec Kioo Radio 98.1 FM!\n📻 Méditation Matinale: 5h00 GMT\n📖 À Travers la Bible: 6h00 GMT\n\nPour quoi êtes-vous reconnaissant aujourd'hui? Partagez ci-dessous! 👇\n\n🎧 Écoutez GRATUITEMENT: kiooradio.org\n#KiooRadio #BénédictionMatinale #Foi`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Program Spotlight - Love & Faith */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-red-600 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">❤️ Program Spotlight: Love & Faith with Bishop Michael Thomas</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    ❤️ LOVE & FAITH with Bishop Michael Thomas 📖<br/><br/>
                    "And now these three remain: faith, hope and love. But the greatest of these is love." - 1 Corinthians 13:13<br/><br/>
                    Join Bishop Michael Thomas every morning at 7:00 AM GMT for powerful Bible teaching that transforms lives!<br/><br/>
                    🎙️ Love & Faith has touched thousands across West Africa with messages of hope and redemption.<br/><br/>
                    How has God shown His love to you this week? Share your testimony! 👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #LoveAndFaith #BishopMichaelThomas #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`❤️ LOVE & FAITH with Bishop Michael Thomas 📖\n\n"And now these three remain: faith, hope and love. But the greatest of these is love." - 1 Corinthians 13:13\n\nJoin Bishop Michael Thomas every morning at 7:00 AM GMT for powerful Bible teaching that transforms lives!\n\n🎙️ Love & Faith has touched thousands across West Africa with messages of hope and redemption.\n\nHow has God shown His love to you this week? Share your testimony! 👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#LoveAndFaith #BishopMichaelThomas #KiooRadio`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    ❤️ AMOUR & FOI avec l'Évêque Michael Thomas 📖<br/><br/>
                    "Maintenant donc ces trois choses demeurent: la foi, l'espérance, l'amour; mais la plus grande de ces choses, c'est l'amour." - 1 Corinthiens 13:13<br/><br/>
                    Rejoignez l'Évêque Michael Thomas chaque matin à 7h00 GMT pour un enseignement biblique puissant!<br/><br/>
                    🎙️ Amour & Foi a touché des milliers de personnes à travers l'Afrique de l'Ouest avec des messages d'espoir.<br/><br/>
                    Comment Dieu vous a-t-il montré Son amour cette semaine? Partagez! 👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #AmourEtFoi #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`❤️ AMOUR & FOI avec l'Évêque Michael Thomas 📖\n\n"Maintenant donc ces trois choses demeurent: la foi, l'espérance, l'amour; mais la plus grande de ces choses, c'est l'amour." - 1 Corinthiens 13:13\n\nRejoignez l'Évêque Michael Thomas chaque matin à 7h00 GMT pour un enseignement biblique puissant!\n\n🎙️ Amour & Foi a touché des milliers de personnes à travers l'Afrique de l'Ouest avec des messages d'espoir.\n\nComment Dieu vous a-t-il montré Son amour cette semaine? Partagez! 👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#AmourEtFoi #KiooRadio`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Program Spotlight - Thru the Bible */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-indigo-600 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">📖 Program Spotlight: Thru the Bible (TTB)</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    📖 THRU THE BIBLE - Journey Through God's Word! 🌍<br/><br/>
                    Take a journey through the entire Bible with Thru the Bible! This beloved program has been guiding believers through Scripture for generations.<br/><br/>
                    🕐 Listen on Kioo Radio:<br/>
                    • English: 6:00 AM & 9:00 PM GMT<br/>
                    • French: 2:00 PM & 10:00 PM GMT<br/>
                    • Kissi: 8:00 AM & 8:00 PM GMT<br/><br/>
                    What book of the Bible are you studying? Let us know! 👇<br/><br/>
                    📻 98.1 FM | kiooradio.org<br/>
                    #ThruTheBible #BibleStudy #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`📖 THRU THE BIBLE - Journey Through God's Word! 🌍\n\nTake a journey through the entire Bible with Thru the Bible! This beloved program has been guiding believers through Scripture for generations.\n\n🕐 Listen on Kioo Radio:\n• English: 6:00 AM & 9:00 PM GMT\n• French: 2:00 PM & 10:00 PM GMT\n• Kissi: 8:00 AM & 8:00 PM GMT\n\nWhat book of the Bible are you studying? Let us know! 👇\n\n📻 98.1 FM | kiooradio.org\n#ThruTheBible #BibleStudy #KiooRadio`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    📖 À TRAVERS LA BIBLE - Voyage à travers la Parole de Dieu! 🌍<br/><br/>
                    Faites un voyage à travers toute la Bible! Ce programme bien-aimé guide les croyants à travers les Écritures depuis des générations.<br/><br/>
                    🕐 Écoutez sur Kioo Radio:<br/>
                    • Anglais: 6h00 & 21h00 GMT<br/>
                    • Français: 14h00 & 22h00 GMT<br/>
                    • Kissi: 8h00 & 20h00 GMT<br/><br/>
                    Quel livre de la Bible étudiez-vous? Dites-nous! 👇<br/><br/>
                    📻 98.1 FM | kiooradio.org<br/>
                    #ATraversLaBible #EtudeBiblique #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`📖 À TRAVERS LA BIBLE - Voyage à travers la Parole de Dieu! 🌍\n\nFaites un voyage à travers toute la Bible! Ce programme bien-aimé guide les croyants à travers les Écritures depuis des générations.\n\n🕐 Écoutez sur Kioo Radio:\n• Anglais: 6h00 & 21h00 GMT\n• Français: 14h00 & 22h00 GMT\n• Kissi: 8h00 & 20h00 GMT\n\nQuel livre de la Bible étudiez-vous? Dites-nous! 👇\n\n📻 98.1 FM | kiooradio.org\n#ATraversLaBible #EtudeBiblique #KiooRadio`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Program Spotlight - Guidelines for Living */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-teal-600 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">🧭 Program Spotlight: Guidelines for Living</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    🧭 GUIDELINES FOR LIVING - Wisdom for Daily Life! 💡<br/><br/>
                    Need practical guidance for life's challenges? Guidelines for Living brings you Biblical wisdom that works!<br/><br/>
                    In just 5 minutes, you'll receive encouragement and direction for:<br/>
                    ✝️ Family relationships<br/>
                    ✝️ Work and finances<br/>
                    ✝️ Personal growth<br/>
                    ✝️ Faith journey<br/><br/>
                    🕐 Daily at 11:00 AM & 3:30 PM GMT on Kioo Radio<br/><br/>
                    What life challenge are you facing today? We're praying for you! 🙏👇<br/><br/>
                    📻 kiooradio.org<br/>
                    #GuidelinesForLiving #DailyWisdom #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`🧭 GUIDELINES FOR LIVING - Wisdom for Daily Life! 💡\n\nNeed practical guidance for life's challenges? Guidelines for Living brings you Biblical wisdom that works!\n\nIn just 5 minutes, you'll receive encouragement and direction for:\n✝️ Family relationships\n✝️ Work and finances\n✝️ Personal growth\n✝️ Faith journey\n\n🕐 Daily at 11:00 AM & 3:30 PM GMT on Kioo Radio\n\nWhat life challenge are you facing today? We're praying for you! 🙏👇\n\n📻 kiooradio.org\n#GuidelinesForLiving #DailyWisdom #KiooRadio`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    🧭 DIRECTIVES POUR LA VIE - Sagesse pour le Quotidien! 💡<br/><br/>
                    Besoin de conseils pratiques pour les défis de la vie? Directives pour la Vie vous apporte la sagesse biblique qui fonctionne!<br/><br/>
                    En seulement 5 minutes, recevez encouragement et direction pour:<br/>
                    ✝️ Relations familiales<br/>
                    ✝️ Travail et finances<br/>
                    ✝️ Croissance personnelle<br/>
                    ✝️ Parcours de foi<br/><br/>
                    🕐 Tous les jours à 11h00 & 15h30 GMT sur Kioo Radio<br/><br/>
                    Quel défi affrontez-vous aujourd'hui? Nous prions pour vous! 🙏👇<br/><br/>
                    📻 kiooradio.org<br/>
                    #DirectivesPourLaVie #SagesseQuotidienne #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`🧭 DIRECTIVES POUR LA VIE - Sagesse pour le Quotidien! 💡\n\nBesoin de conseils pratiques pour les défis de la vie? Directives pour la Vie vous apporte la sagesse biblique qui fonctionne!\n\nEn seulement 5 minutes, recevez encouragement et direction pour:\n✝️ Relations familiales\n✝️ Travail et finances\n✝️ Croissance personnelle\n✝️ Parcours de foi\n\n🕐 Tous les jours à 11h00 & 15h30 GMT sur Kioo Radio\n\nQuel défi affrontez-vous aujourd'hui? Nous prions pour vous! 🙏👇\n\n📻 kiooradio.org\n#DirectivesPourLaVie #SagesseQuotidienne #KiooRadio`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Program Spotlight - Reset by Bonnie Sala */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-pink-600 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">🔄 Program Spotlight: Reset by Bonnie Sala</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    🔄 RESET with Bonnie Sala - A Fresh Start Every Day! ✨<br/><br/>
                    Feeling overwhelmed? Need a spiritual reset? Bonnie Sala's short, powerful devotionals will refresh your soul!<br/><br/>
                    "Come to me, all you who are weary and burdened, and I will give you rest." - Matthew 11:28<br/><br/>
                    🕐 Catch RESET 3 times daily on Kioo Radio:<br/>
                    • 11:30 AM GMT<br/>
                    • 6:00 PM GMT<br/>
                    • 10:00 PM GMT<br/><br/>
                    Tag someone who needs a RESET today! 💕👇<br/><br/>
                    📻 kiooradio.org<br/>
                    #ResetWithBonnieSala #FreshStart #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`🔄 RESET with Bonnie Sala - A Fresh Start Every Day! ✨\n\nFeeling overwhelmed? Need a spiritual reset? Bonnie Sala's short, powerful devotionals will refresh your soul!\n\n"Come to me, all you who are weary and burdened, and I will give you rest." - Matthew 11:28\n\n🕐 Catch RESET 3 times daily on Kioo Radio:\n• 11:30 AM GMT\n• 6:00 PM GMT\n• 10:00 PM GMT\n\nTag someone who needs a RESET today! 💕👇\n\n📻 kiooradio.org\n#ResetWithBonnieSala #FreshStart #KiooRadio`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    🔄 RESET avec Bonnie Sala - Un Nouveau Départ Chaque Jour! ✨<br/><br/>
                    Vous vous sentez submergé? Besoin d'un renouveau spirituel? Les dévotions courtes et puissantes de Bonnie Sala rafraîchiront votre âme!<br/><br/>
                    "Venez à moi, vous tous qui êtes fatigués et chargés, et je vous donnerai du repos." - Matthieu 11:28<br/><br/>
                    🕐 Écoutez RESET 3 fois par jour sur Kioo Radio:<br/>
                    • 11h30 GMT<br/>
                    • 18h00 GMT<br/>
                    • 22h00 GMT<br/><br/>
                    Identifiez quelqu'un qui a besoin d'un RESET aujourd'hui! 💕👇<br/><br/>
                    📻 kiooradio.org<br/>
                    #ResetAvecBonnieSala #NouveauDépart #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`🔄 RESET avec Bonnie Sala - Un Nouveau Départ Chaque Jour! ✨\n\nVous vous sentez submergé? Besoin d'un renouveau spirituel? Les dévotions courtes et puissantes de Bonnie Sala rafraîchiront votre âme!\n\n"Venez à moi, vous tous qui êtes fatigués et chargés, et je vous donnerai du repos." - Matthieu 11:28\n\n🕐 Écoutez RESET 3 fois par jour sur Kioo Radio:\n• 11h30 GMT\n• 18h00 GMT\n• 22h00 GMT\n\nIdentifiez quelqu'un qui a besoin d'un RESET aujourd'hui! 💕👇\n\n📻 kiooradio.org\n#ResetAvecBonnieSala #NouveauDépart #KiooRadio`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Program Spotlight - Renaissance */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-purple-600 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">🎙️ Program Spotlight: Renaissance with Isabelle Kenne</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    🎙️ RENAISSANCE with Isabelle Kenne 🇫🇷<br/><br/>
                    "La musique qui relève et restaure" - Music that lifts up and restores!<br/><br/>
                    Join Isabelle Kenne every FRIDAY for an hour of:<br/>
                    🎵 Uplifting French music<br/>
                    💬 Community discussions<br/>
                    🙏 Inspirational messages<br/>
                    📞 Interactive conversations<br/><br/>
                    🕐 Fridays 4:00 - 5:00 PM GMT<br/><br/>
                    Are you tuning in this Friday? Let us know where you're listening from! 🌍👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #Renaissance #IsabelleKenne #KiooRadio #FrenchProgram
                  </p>
                  <button onClick={() => copyToClipboard(`🎙️ RENAISSANCE with Isabelle Kenne 🇫🇷\n\n"La musique qui relève et restaure" - Music that lifts up and restores!\n\nJoin Isabelle Kenne every FRIDAY for an hour of:\n🎵 Uplifting French music\n💬 Community discussions\n🙏 Inspirational messages\n📞 Interactive conversations\n\n🕐 Fridays 4:00 - 5:00 PM GMT\n\nAre you tuning in this Friday? Let us know where you're listening from! 🌍👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#Renaissance #IsabelleKenne #KiooRadio #FrenchProgram`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    🎙️ RENAISSANCE avec Isabelle Kenne 🇫🇷<br/><br/>
                    "La musique qui relève et restaure" ✨<br/><br/>
                    Rejoignez Isabelle Kenne chaque VENDREDI pour une heure de:<br/>
                    🎵 Musique française édifiante<br/>
                    💬 Discussions communautaires<br/>
                    🙏 Messages inspirants<br/>
                    📞 Conversations interactives<br/><br/>
                    🕐 Vendredis 16h00 - 17h00 GMT<br/><br/>
                    Serez-vous à l'écoute ce vendredi? D'où nous écoutez-vous? 🌍👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #Renaissance #IsabelleKenne #KiooRadio #ProgrammeFrançais
                  </p>
                  <button onClick={() => copyToClipboard(`🎙️ RENAISSANCE avec Isabelle Kenne 🇫🇷\n\n"La musique qui relève et restaure" ✨\n\nRejoignez Isabelle Kenne chaque VENDREDI pour une heure de:\n🎵 Musique française édifiante\n💬 Discussions communautaires\n🙏 Messages inspirants\n📞 Conversations interactives\n\n🕐 Vendredis 16h00 - 17h00 GMT\n\nSerez-vous à l'écoute ce vendredi? D'où nous écoutez-vous? 🌍👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#Renaissance #IsabelleKenne #KiooRadio #ProgrammeFrançais`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Program Spotlight - Truth for Life */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-blue-800 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">📚 Program Spotlight: Truth for Life</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    📚 TRUTH FOR LIFE - Teaching the Bible Every Day! 🌟<br/><br/>
                    "Your word is a lamp for my feet, a light on my path." - Psalm 119:105<br/><br/>
                    Truth for Life brings clear, relevant Bible teaching to help you grow in your faith and live for God's glory.<br/><br/>
                    🎯 What you'll learn:<br/>
                    • Deep Biblical truths made simple<br/>
                    • Practical application for daily life<br/>
                    • Foundation for spiritual growth<br/><br/>
                    What Bible question would you like answered? Ask us! 👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #TruthForLife #BibleTeaching #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`📚 TRUTH FOR LIFE - Teaching the Bible Every Day! 🌟\n\n"Your word is a lamp for my feet, a light on my path." - Psalm 119:105\n\nTruth for Life brings clear, relevant Bible teaching to help you grow in your faith and live for God's glory.\n\n🎯 What you'll learn:\n• Deep Biblical truths made simple\n• Practical application for daily life\n• Foundation for spiritual growth\n\nWhat Bible question would you like answered? Ask us! 👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#TruthForLife #BibleTeaching #KiooRadio`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    📚 VÉRITÉ POUR LA VIE - Enseignement Biblique Quotidien! 🌟<br/><br/>
                    "Ta parole est une lampe à mes pieds, et une lumière sur mon sentier." - Psaume 119:105<br/><br/>
                    Vérité pour la Vie apporte un enseignement biblique clair et pertinent pour vous aider à grandir dans votre foi.<br/><br/>
                    🎯 Ce que vous apprendrez:<br/>
                    • Vérités bibliques profondes simplifiées<br/>
                    • Application pratique quotidienne<br/>
                    • Fondement pour la croissance spirituelle<br/><br/>
                    Quelle question biblique aimeriez-vous voir répondue? Demandez-nous! 👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #VéritéPourLaVie #EnseignementBiblique #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`📚 VÉRITÉ POUR LA VIE - Enseignement Biblique Quotidien! 🌟\n\n"Ta parole est une lampe à mes pieds, et une lumière sur mon sentier." - Psaume 119:105\n\nVérité pour la Vie apporte un enseignement biblique clair et pertinent pour vous aider à grandir dans votre foi.\n\n🎯 Ce que vous apprendrez:\n• Vérités bibliques profondes simplifiées\n• Application pratique quotidienne\n• Fondement pour la croissance spirituelle\n\nQuelle question biblique aimeriez-vous voir répondue? Demandez-nous! 👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#VéritéPourLaVie #EnseignementBiblique #KiooRadio`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Program Spotlight - Mission Afrique Pour Christ */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-green-700 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">🌍 Program Spotlight: Mission Afrique Pour Christ</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    🌍 MISSION AFRIQUE POUR CHRIST - Africa for Christ! 🙏<br/><br/>
                    "Go into all the world and preach the gospel to all creation." - Mark 16:15<br/><br/>
                    Mission Afrique Pour Christ brings the Gospel message to French-speaking Africa with passion and power!<br/><br/>
                    🎯 Our mission:<br/>
                    • Spreading the Good News across Africa<br/>
                    • Discipling new believers<br/>
                    • Building strong churches<br/>
                    • Transforming communities through Christ<br/><br/>
                    Are you part of the mission? Share how God is using you! 🌍👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #MissionAfriquePourChrist #AfricaForChrist #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`🌍 MISSION AFRIQUE POUR CHRIST - Africa for Christ! 🙏\n\n"Go into all the world and preach the gospel to all creation." - Mark 16:15\n\nMission Afrique Pour Christ brings the Gospel message to French-speaking Africa with passion and power!\n\n🎯 Our mission:\n• Spreading the Good News across Africa\n• Discipling new believers\n• Building strong churches\n• Transforming communities through Christ\n\nAre you part of the mission? Share how God is using you! 🌍👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#MissionAfriquePourChrist #AfricaForChrist #KiooRadio`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    🌍 MISSION AFRIQUE POUR CHRIST - L'Afrique pour Christ! 🙏<br/><br/>
                    "Allez par tout le monde, et prêchez la bonne nouvelle à toute la création." - Marc 16:15<br/><br/>
                    Mission Afrique Pour Christ apporte le message de l'Évangile à l'Afrique francophone avec passion et puissance!<br/><br/>
                    🎯 Notre mission:<br/>
                    • Répandre la Bonne Nouvelle à travers l'Afrique<br/>
                    • Former les nouveaux croyants<br/>
                    • Bâtir des églises solides<br/>
                    • Transformer les communautés par Christ<br/><br/>
                    Faites-vous partie de la mission? Partagez comment Dieu vous utilise! 🌍👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #MissionAfriquePourChrist #LAfriquePourChrist #KiooRadio
                  </p>
                  <button onClick={() => copyToClipboard(`🌍 MISSION AFRIQUE POUR CHRIST - L'Afrique pour Christ! 🙏\n\n"Allez par tout le monde, et prêchez la bonne nouvelle à toute la création." - Marc 16:15\n\nMission Afrique Pour Christ apporte le message de l'Évangile à l'Afrique francophone avec passion et puissance!\n\n🎯 Notre mission:\n• Répandre la Bonne Nouvelle à travers l'Afrique\n• Former les nouveaux croyants\n• Bâtir des églises solides\n• Transformer les communautés par Christ\n\nFaites-vous partie de la mission? Partagez comment Dieu vous utilise! 🌍👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#MissionAfriquePourChrist #LAfriquePourChrist #KiooRadio`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Program Spotlight - Makona Talk Show */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-yellow-600 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">🎤 Program Spotlight: Makona Talk Show</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    🎤 MAKONA TALK SHOW - Your Voice, Your Community! 📻<br/><br/>
                    Every SATURDAY morning, the Makona River Region comes together!<br/><br/>
                    🕐 Saturdays 6:00 - 9:00 AM GMT<br/>
                    🗣️ In English, French & Kissi<br/><br/>
                    3 hours of:<br/>
                    📞 Call-ins from listeners<br/>
                    💬 Community discussions<br/>
                    🎵 Music requests<br/>
                    📰 Local news and updates<br/>
                    🙏 Prayer and encouragement<br/><br/>
                    What topic should we discuss this Saturday? Tell us! 👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #MakonaTalkShow #CommunityRadio #KiooRadio #Saturday
                  </p>
                  <button onClick={() => copyToClipboard(`🎤 MAKONA TALK SHOW - Your Voice, Your Community! 📻\n\nEvery SATURDAY morning, the Makona River Region comes together!\n\n🕐 Saturdays 6:00 - 9:00 AM GMT\n🗣️ In English, French & Kissi\n\n3 hours of:\n📞 Call-ins from listeners\n💬 Community discussions\n🎵 Music requests\n📰 Local news and updates\n🙏 Prayer and encouragement\n\nWhat topic should we discuss this Saturday? Tell us! 👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#MakonaTalkShow #CommunityRadio #KiooRadio #Saturday`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    🎤 MAKONA TALK SHOW - Votre Voix, Votre Communauté! 📻<br/><br/>
                    Chaque SAMEDI matin, la Région de la Rivière Makona se réunit!<br/><br/>
                    🕐 Samedis 6h00 - 9h00 GMT<br/>
                    🗣️ En anglais, français et kissi<br/><br/>
                    3 heures de:<br/>
                    📞 Appels des auditeurs<br/>
                    💬 Discussions communautaires<br/>
                    🎵 Demandes musicales<br/>
                    📰 Nouvelles et mises à jour locales<br/>
                    🙏 Prière et encouragement<br/><br/>
                    Quel sujet devrions-nous discuter ce samedi? Dites-nous! 👇<br/><br/>
                    📻 Kioo Radio 98.1 FM | kiooradio.org<br/>
                    #MakonaTalkShow #RadioCommunautaire #KiooRadio #Samedi
                  </p>
                  <button onClick={() => copyToClipboard(`🎤 MAKONA TALK SHOW - Votre Voix, Votre Communauté! 📻\n\nChaque SAMEDI matin, la Région de la Rivière Makona se réunit!\n\n🕐 Samedis 6h00 - 9h00 GMT\n🗣️ En anglais, français et kissi\n\n3 heures de:\n📞 Appels des auditeurs\n💬 Discussions communautaires\n🎵 Demandes musicales\n📰 Nouvelles et mises à jour locales\n🙏 Prière et encouragement\n\nQuel sujet devrions-nous discuter ce samedi? Dites-nous! 👇\n\n📻 Kioo Radio 98.1 FM | kiooradio.org\n#MakonaTalkShow #RadioCommunautaire #KiooRadio #Samedi`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Evening Encouragement */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gray-800 text-white px-6 py-3">
                <h3 className="font-bold flex items-center gap-2">🌙 Evening Encouragement Post</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="text-sm text-blue-600 font-bold mb-1">🇬🇧 ENGLISH</p>
                  <p className="text-gray-700">
                    🌙 Good evening, family! 🙏<br/><br/>
                    As this day comes to an end, remember:<br/><br/>
                    "The LORD your God is with you, the Mighty Warrior who saves. He will take great delight in you; in his love he will no longer rebuke you, but will rejoice over you with singing." - Zephaniah 3:17<br/><br/>
                    God is with you. He delights in you. Rest in His love tonight. 💕<br/><br/>
                    📻 End your day with Kioo Radio's Evening Devotional at 11:30 PM GMT<br/><br/>
                    What are you trusting God for tonight? Share and let's pray together! 🙏👇<br/><br/>
                    #GoodNight #GodIsWithYou #KiooRadio #EveningBlessing
                  </p>
                  <button onClick={() => copyToClipboard(`🌙 Good evening, family! 🙏\n\nAs this day comes to an end, remember:\n\n"The LORD your God is with you, the Mighty Warrior who saves. He will take great delight in you; in his love he will no longer rebuke you, but will rejoice over you with singing." - Zephaniah 3:17\n\nGod is with you. He delights in you. Rest in His love tonight. 💕\n\n📻 End your day with Kioo Radio's Evening Devotional at 11:30 PM GMT\n\nWhat are you trusting God for tonight? Share and let's pray together! 🙏👇\n\n#GoodNight #GodIsWithYou #KiooRadio #EveningBlessing`)} className="mt-2 px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                    📋 Copy English
                  </button>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-green-600 font-bold mb-1">🇫🇷 FRANÇAIS</p>
                  <p className="text-gray-700">
                    🌙 Bonsoir, famille! 🙏<br/><br/>
                    Alors que cette journée se termine, souvenez-vous:<br/><br/>
                    "L'Éternel, ton Dieu, est au milieu de toi, comme un héros qui sauve; il fera de toi sa plus grande joie; il gardera le silence dans son amour; il aura pour toi des transports d'allégresse." - Sophonie 3:17<br/><br/>
                    Dieu est avec vous. Il prend plaisir en vous. Reposez-vous dans Son amour ce soir. 💕<br/><br/>
                    📻 Terminez votre journée avec la Méditation du Soir de Kioo Radio à 23h30 GMT<br/><br/>
                    Pour quoi faites-vous confiance à Dieu ce soir? Partagez et prions ensemble! 🙏👇<br/><br/>
                    #BonneNuit #DieuEstAvecVous #KiooRadio #BénédictionDuSoir
                  </p>
                  <button onClick={() => copyToClipboard(`🌙 Bonsoir, famille! 🙏\n\nAlors que cette journée se termine, souvenez-vous:\n\n"L'Éternel, ton Dieu, est au milieu de toi, comme un héros qui sauve; il fera de toi sa plus grande joie; il gardera le silence dans son amour; il aura pour toi des transports d'allégresse." - Sophonie 3:17\n\nDieu est avec vous. Il prend plaisir en vous. Reposez-vous dans Son amour ce soir. 💕\n\n📻 Terminez votre journée avec la Méditation du Soir de Kioo Radio à 23h30 GMT\n\nPour quoi faites-vous confiance à Dieu ce soir? Partagez et prions ensemble! 🙏👇\n\n#BonneNuit #DieuEstAvecVous #KiooRadio #BénédictionDuSoir`)} className="mt-2 px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📋 Copy French
                  </button>
                </div>
              </div>
            </div>

            {/* Reminder */}
            <div className="bg-yellow-100 border-2 border-yellow-400 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-2 text-yellow-800 flex items-center gap-2">
                ⚠️ Before Posting - Remember!
              </h3>
              <ul className="text-yellow-700 space-y-1">
                <li>✅ Copy the post you want to use</li>
                <li>✅ Customize with current date/event if needed</li>
                <li>✅ Submit to "Post Approval" tab</li>
                <li>✅ Wait for supervisor approval</li>
                <li>✅ Only post AFTER approval received</li>
                <li>❌ NEVER post without approval</li>
              </ul>
            </div>
          </div>
        )}

        {/* Content Guidelines Tab - NEW */}
        {activeTab === 'guidelines' && (
          <div className="space-y-6">
            {/* Main Warning Banner */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 text-white">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                🚨 CRITICAL: Content Guidelines
              </h2>
              <p className="text-red-100 text-lg">
                ALL content must be reviewed and approved by a human supervisor before posting or sending.
              </p>
            </div>

            {/* What We Post */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-green-600 flex items-center gap-2">
                ✅ APPROVED CONTENT THEMES
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { icon: '⛪', title: 'Christian Faith', desc: 'Bible verses, prayer, devotionals, testimonies, worship' },
                  { icon: '💪', title: 'Encouragement', desc: 'Uplifting messages, hope, inspiration, motivation' },
                  { icon: '🤝', title: 'Community Building', desc: 'Unity, togetherness, support, fellowship, local events' },
                  { icon: '👨‍👩‍👧‍👦', title: 'Family Values', desc: 'Marriage, parenting, youth guidance, elder wisdom' },
                  { icon: '📻', title: 'Radio Programs', desc: 'Schedule updates, program highlights, listener stories' },
                  { icon: '🙏', title: 'Prayer Requests', desc: 'Community prayers, healing, blessings, thanksgiving' },
                  { icon: '📖', title: 'Bible Teaching', desc: 'Scripture study, spiritual growth, discipleship' },
                  { icon: '🎵', title: 'Gospel Music', desc: 'Worship songs, hymns, local Christian artists' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <p className="font-bold text-green-800">{item.title}</p>
                      <p className="text-sm text-green-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* What We DO NOT Post */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-red-600 flex items-center gap-2">
                🚫 STRICTLY PROHIBITED CONTENT
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { icon: '🏛️', title: 'Political Content', desc: 'NO political parties, candidates, elections, government criticism' },
                  { icon: '⚔️', title: 'Divisive Topics', desc: 'NO tribal conflicts, ethnic tensions, controversial debates' },
                  { icon: '😠', title: 'Negative Speech', desc: 'NO criticism of individuals, groups, or organizations' },
                  { icon: '📰', title: 'Unverified News', desc: 'NO rumors, unconfirmed stories, or sensational claims' },
                  { icon: '💰', title: 'Commercial Spam', desc: 'NO excessive promotions, sales pitches, or advertisements' },
                  { icon: '🗣️', title: 'Controversial Opinions', desc: 'NO divisive social or cultural commentary' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <p className="font-bold text-red-800">{item.title}</p>
                      <p className="text-sm text-red-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Approval Process */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                📋 MANDATORY APPROVAL PROCESS
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">1</div>
                  <div>
                    <p className="font-bold">Draft Content</p>
                    <p className="text-blue-200 text-sm">Write your post/message in the "Post Approval" tab</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">2</div>
                  <div>
                    <p className="font-bold">Submit for Review</p>
                    <p className="text-blue-200 text-sm">Send to supervisor for approval</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">3</div>
                  <div>
                    <p className="font-bold">Human Approval Required</p>
                    <p className="text-blue-200 text-sm">Supervisor reviews and approves/rejects with comments</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">4</div>
                  <div>
                    <p className="font-bold">Post Only After Approval</p>
                    <p className="text-blue-200 text-sm">NEVER post content that hasn't been approved</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Reference Card */}
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-3 text-yellow-800 flex items-center gap-2">
                💡 Quick Check Before Posting
              </h3>
              <div className="space-y-2">
                {[
                  'Is this content Christ-centered and encouraging?',
                  'Does this build community and unity?',
                  'Is this FREE from political content or opinions?',
                  'Would this be appropriate in a church service?',
                  'Has this been APPROVED by a supervisor?'
                ].map((question, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-yellow-600">☐</span>
                    <span className="text-yellow-800">{question}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-yellow-700 font-bold">
                If ANY answer is NO → DO NOT POST
              </p>
            </div>
          </div>
        )}

        {/* Post Approval Tab - NEW */}
        {activeTab === 'approval' && (
          <div className="space-y-6">
            {/* Warning Banner */}
            <div className="bg-orange-100 border-2 border-orange-400 rounded-xl p-4">
              <p className="text-orange-800 font-bold flex items-center gap-2">
                ⚠️ REMINDER: All posts require human supervisor approval before publishing
              </p>
            </div>

            {/* Create New Post */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                ✍️ Create New Post for Approval
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                  <select 
                    value={newPostPlatform}
                    onChange={(e) => setNewPostPlatform(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="facebook">Facebook Post</option>
                    <option value="facebook_comment">Facebook Comment</option>
                    <option value="whatsapp">WhatsApp Message</option>
                    <option value="direct_message">Direct Message</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg h-40"
                    placeholder="Write your post content here... Remember: Christ-centered, encouraging, community-building only!"
                  />
                </div>

                {/* Content Checklist */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold mb-2">✅ Pre-submission Checklist:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="rounded" />
                      <span>Content is Christ-centered</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="rounded" />
                      <span>Content is encouraging</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="rounded" />
                      <span>NO political content</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="rounded" />
                      <span>Builds community unity</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="rounded" />
                      <span>Appropriate for all ages</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="rounded" />
                      <span>No controversial topics</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    📤 Submit for Approval
                  </button>
                  <button className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                    💾 Save as Draft
                  </button>
                </div>
              </div>
            </div>

            {/* Pending Approval Queue */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                📋 Posts Pending Approval
              </h3>
              
              <div className="space-y-4">
                {/* Example pending post */}
                <div className="border-2 border-yellow-300 rounded-lg p-4 bg-yellow-50">
                  <div className="flex justify-between items-start mb-2">
                    <span className="px-3 py-1 bg-yellow-200 text-yellow-800 rounded-full text-sm font-bold">
                      ⏳ PENDING REVIEW
                    </span>
                    <span className="text-sm text-gray-500">Submitted: Today 10:30 AM</span>
                  </div>
                  <p className="text-gray-700 mb-3">
                    🙏 Good morning family! Start your day with prayer and worship on Kioo Radio 98.1 FM. 
                    "This is the day the Lord has made; let us rejoice and be glad in it." - Psalm 118:24
                    📻 Stream: kiooradio.org
                  </p>
                  <div className="flex gap-2">
                    <button className="px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                      ✅ Approve
                    </button>
                    <button className="px-4 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                      ❌ Reject
                    </button>
                    <button className="px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                      ✏️ Request Edit
                    </button>
                  </div>
                </div>

                {/* Approved post example */}
                <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                  <div className="flex justify-between items-start mb-2">
                    <span className="px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm font-bold">
                      ✅ APPROVED - Ready to Post
                    </span>
                    <span className="text-sm text-gray-500">Approved by: Admin | Jan 5, 2026</span>
                  </div>
                  <p className="text-gray-700 mb-3">
                    ⛪ Join us for LIVE Sunday Service on Kioo Radio! 7:00-10:00 AM GMT. 
                    Worship with us from anywhere in the world. God bless you! 📻 kiooradio.org
                  </p>
                  <button className="px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    📤 Post Now
                  </button>
                </div>
              </div>
            </div>

            {/* Approval Log */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                📜 Recent Approval History
              </h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Content (Preview)</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Approved By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-2">Jan 5</td>
                    <td className="p-2 truncate max-w-xs">Sunday Service announcement...</td>
                    <td className="p-2"><span className="text-green-600">✅ Approved</span></td>
                    <td className="p-2">Admin</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">Jan 4</td>
                    <td className="p-2 truncate max-w-xs">New Year blessing post...</td>
                    <td className="p-2"><span className="text-green-600">✅ Approved</span></td>
                    <td className="p-2">Admin</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">Jan 3</td>
                    <td className="p-2 truncate max-w-xs">Community event post...</td>
                    <td className="p-2"><span className="text-red-600">❌ Rejected</span></td>
                    <td className="p-2">Admin</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Daily Log Tab */}
        {activeTab === 'daily' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              📋 Daily Scout Log
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { key: 'groupsFound', label: 'Groups Found', icon: '🔍' },
                { key: 'groupsJoined', label: 'Groups Joined', icon: '✅' },
                { key: 'commentsPosted', label: 'Comments Posted', icon: '💬' },
                { key: 'messagesSent', label: 'Messages Sent', icon: '📤' },
                { key: 'leadsCollected', label: 'Leads Collected', icon: '👥' },
                { key: 'positiveResponses', label: 'Positive Responses', icon: '👍' },
                { key: 'whatsappJoins', label: 'WhatsApp Joins', icon: '📱' }
              ].map(field => (
                <div key={field.key} className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm text-gray-600 mb-1">
                    {field.icon} {field.label}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={dailyLog[field.key]}
                    onChange={(e) => setDailyLog({...dailyLog, [field.key]: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg text-2xl font-bold text-center"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🎯 Today's Wins
                </label>
                <textarea
                  value={dailyLog.wins}
                  onChange={(e) => setDailyLog({...dailyLog, wins: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg h-24"
                  placeholder="What went well today?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🚧 Challenges
                </label>
                <textarea
                  value={dailyLog.challenges}
                  onChange={(e) => setDailyLog({...dailyLog, challenges: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg h-24"
                  placeholder="Any obstacles encountered?"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                📝 Notes for Tomorrow
              </label>
              <textarea
                value={dailyLog.notes}
                onChange={(e) => setDailyLog({...dailyLog, notes: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg h-20"
                placeholder="What should be the focus tomorrow?"
              />
            </div>

            <div className="mt-6 flex gap-3">
              <button className="px-6 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors">
                💾 Save Daily Log
              </button>
              <button className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                📊 View Weekly Report
              </button>
            </div>
          </div>
        )}

        {/* Message Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                💬 Outreach Message Templates
              </h2>
              <p className="text-gray-600 mb-4">
                Click any template to copy. Remember to personalize with the recipient's name and group!
              </p>
            </div>

            {messageTemplates.map(template => (
              <div key={template.id} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-lg">#{template.id} - {template.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded ${
                      template.language === 'French' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {template.language}
                    </span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(template.template)}
                    className="px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors text-sm"
                  >
                    📋 Copy
                  </button>
                </div>
                <pre className="bg-gray-50 p-4 rounded-lg text-sm whitespace-pre-wrap font-sans">
                  {template.template}
                </pre>
              </div>
            ))}
          </div>
        )}

        {/* Search Guide Tab */}
        {activeTab === 'search' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              🔍 Facebook Search Guide
            </h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-bold text-blue-800 mb-2">📌 How to Search:</h3>
              <ol className="text-blue-700 space-y-1 text-sm">
                <li>1. Go to facebook.com/search</li>
                <li>2. Click "Groups" filter on the left</li>
                <li>3. Paste the search query</li>
                <li>4. Sort by "Most Active"</li>
                <li>5. Join groups with 500+ members first</li>
              </ol>
            </div>

            <div className="space-y-4">
              {searchQueries.map((area, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <h3 className="font-bold text-lg mb-2">📍 {area.area}</h3>
                  <div className="flex flex-wrap gap-2">
                    {area.queries.map((query, qIdx) => (
                      <button
                        key={qIdx}
                        onClick={() => copyToClipboard(query)}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm transition-colors"
                      >
                        {query} 📋
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-bold text-yellow-800 mb-2">💡 Pro Tips:</h3>
              <ul className="text-yellow-700 space-y-1 text-sm">
                <li>• Look for groups with recent posts (active communities)</li>
                <li>• Check group rules before posting/commenting</li>
                <li>• Church groups and youth groups are most receptive</li>
                <li>• Diaspora groups have high engagement rates</li>
              </ul>
            </div>
          </div>
        )}

        {/* Target Areas Tab */}
        {activeTab === 'targets' && (
          <div className="space-y-6">
            {/* Countries Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  🇱🇷 Liberia
                </h3>
                <ul className="space-y-2">
                  {targetAreas.liberia.map(town => (
                    <li key={town} className={`flex items-center gap-2 p-2 rounded-lg ${town.includes('Capital') ? 'bg-red-100 border-2 border-red-300' : 'bg-red-50'}`}>
                      <span className="text-2xl">{town.includes('Capital') ? '🏛️' : '📍'}</span>
                      <span className={`${town.includes('Capital') ? 'font-bold' : 'font-medium'}`}>{town}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  🇸🇱 Sierra Leone
                </h3>
                <ul className="space-y-2">
                  {targetAreas.sierraLeone.map(town => (
                    <li key={town} className={`flex items-center gap-2 p-2 rounded-lg ${town.includes('Capital') ? 'bg-green-100 border-2 border-green-300' : 'bg-green-50'}`}>
                      <span className="text-2xl">{town.includes('Capital') ? '🏛️' : '📍'}</span>
                      <span className={`${town.includes('Capital') ? 'font-bold' : 'font-medium'}`}>{town}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  🇬🇳 Guinea
                </h3>
                <ul className="space-y-2">
                  {targetAreas.guinea.map(town => (
                    <li key={town} className={`flex items-center gap-2 p-2 rounded-lg ${town.includes('Capital') ? 'bg-yellow-100 border-2 border-yellow-300' : 'bg-yellow-50'}`}>
                      <span className="text-2xl">{town.includes('Capital') ? '🏛️' : '📍'}</span>
                      <span className={`${town.includes('Capital') ? 'font-bold' : 'font-medium'}`}>{town}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Kissi Diaspora Section */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
              <h3 className="text-2xl font-bold mb-2 flex items-center gap-2">
                🌍 Kissi People Worldwide - Priority Target
              </h3>
              <p className="text-purple-200 mb-4">
                Target every Kissi person or anyone interested in Kissi culture, language, and heritage globally.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {targetAreas.kissiDiaspora.map(location => (
                  <div key={location} className="flex items-center gap-2 p-3 bg-white/10 rounded-lg backdrop-blur">
                    <span className="text-xl">🎯</span>
                    <span className="font-medium">{location}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-4 bg-white/10 rounded-lg">
                <h4 className="font-bold mb-2">🔍 Kissi Search Keywords:</h4>
                <div className="flex flex-wrap gap-2">
                  {['Kissi people', 'Kissi tribe', 'Kissi language', 'Kissi culture', 'Kisi', 'Gissi', 'Kissi diaspora', 'Kissi USA', 'Kissi UK', 'Kissi music', 'Kissi history', 'Kissi homeland'].map(keyword => (
                    <span key={keyword} className="px-3 py-1 bg-white/20 rounded-full text-sm">
                      "{keyword}"
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Engagement Rules Tab */}
        {activeTab === 'rules' && (
          <div className="space-y-6">
            {/* Political Content Warning */}
            <div className="bg-red-600 text-white rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-2 flex items-center gap-2">
                🚫 ZERO TOLERANCE: Political Content
              </h3>
              <p className="text-red-100">
                Kioo Radio is a Christian community radio station. We do NOT engage in political discussions, 
                endorse candidates, or comment on government affairs. Our mission is to spread the Gospel 
                and build community through faith, hope, and love.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-green-600 flex items-center gap-2">
                  ✅ DO These Things
                </h3>
                <ul className="space-y-2">
                  {[
                    'Share Bible verses and Christian encouragement',
                    'Post about prayer, faith, and worship',
                    'Promote community unity and togetherness',
                    'Celebrate local achievements and milestones',
                    'Share Kioo Radio programs and schedules',
                    'Encourage and uplift others',
                    'Read posts before commenting',
                    'Add genuine value to conversations',
                    'Personalize every message with their name',
                    'Respect "No" responses gracefully',
                    'Get human approval before posting',
                    'Wait 24 hours before messaging someone new'
                  ].map((rule, idx) => (
                    <li key={idx} className="flex items-start gap-2 p-2 bg-green-50 rounded-lg">
                      <span className="text-green-600">✓</span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4 text-red-600 flex items-center gap-2">
                  ❌ NEVER Do These Things
                </h3>
                <ul className="space-y-2">
                  {[
                    '🏛️ Post ANY political content or opinions',
                    '🏛️ Mention political parties or candidates',
                    '🏛️ Comment on elections or government',
                    '⚔️ Engage in tribal or ethnic discussions',
                    '😠 Criticize individuals or organizations',
                    '📰 Share unverified news or rumors',
                    'Send identical messages to multiple people',
                    'Post same comment in multiple groups',
                    'Message people who haven\'t interacted',
                    'Use ALL CAPS or excessive emojis',
                    'Send more than 5 cold messages per day',
                    'Post without supervisor approval',
                    'Argue or be pushy if someone declines'
                  ].map((rule, idx) => (
                    <li key={idx} className="flex items-start gap-2 p-2 bg-red-50 rounded-lg">
                      <span className="text-red-600">✗</span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Approval Reminder */}
            <div className="bg-blue-100 border-2 border-blue-400 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-2 text-blue-800 flex items-center gap-2">
                📋 Remember: Human Approval Required
              </h3>
              <p className="text-blue-700">
                Every post, comment, and message must be submitted to the "Post Approval" tab and approved 
                by a supervisor before being published. This ensures all content aligns with our 
                Christ-centered, community-building mission.
              </p>
            </div>
          </div>
        )}

        {/* Quick Stats Footer */}
        <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold mb-4">📊 Daily Targets</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">3+</p>
              <p className="text-sm text-gray-600">Groups Engaged</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-3xl font-bold text-green-600">5+</p>
              <p className="text-sm text-gray-600">Value Comments</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">2-3</p>
              <p className="text-sm text-gray-600">Personal Messages</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-3xl font-bold text-orange-600">5+</p>
              <p className="text-sm text-gray-600">Leads Logged</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineScoutDashboard;
